import { StoreModel } from '@/models/store';
import Vue from 'vue';

// TypeScript declaration for Vue prototype extensions
declare module 'vue/types/vue' {
  interface VueConstructor {
    prototype: {
      $permissionService?: any;
      $appStore?: StoreModel;
      $permissionsInitializing?: boolean;
      $root: any;
    };
  }
}

/**
 * Service for checking feature permissions using the store's rule system
 */
export class PermissionService {
  private store: StoreModel;
  private permissionCache: Map<string, boolean> = new Map();

  constructor(store: StoreModel) {
    this.store = store;
  }

  /**
   * Check if a feature is accessible
   * @param featureCode The feature code to check
   * @returns boolean Whether the feature is accessible
   */
  public hasPermission(featureCode: string): boolean {
    // For lock-type rules, we check if they're unlocked
    const isAllowed = this.store.isValidateRule(featureCode, 'unlocked');
    // Cache the result
    // this.permissionCache.set(featureCode, isAllowed);
    return isAllowed;
  }

  /**
   * Clear permission cache
   */
  public clearCache(): void {
    this.permissionCache.clear();
  }

}

// Use a global Vue instance for sharing the permission service
// This avoids using provide/inject which is causing issues
const permissionServicePlugin = {
  install(Vue: any) {
    Vue.prototype.$permissionService = null;

    Vue.mixin({
      beforeCreate() {
        // Make the permission service accessible through this.$permissionService
        if (this.$options && this.$options.permissionService) {
          this.$permissionService = this.$options.permissionService;
        }
      },
    });
  },
};

Vue.use(permissionServicePlugin);

/**
 * Initialize permission service with a store model
 * @param store The store model instance
 * @returns PermissionService instance
 */
export function providePermission(store: StoreModel): PermissionService {
  const permissionService = new PermissionService(store);
  // Set the service on the Vue prototype instead of using provide/inject
  Vue.prototype.$permissionService = permissionService;
  return permissionService;
}

/**
 * Get the permission service from components
 * @returns PermissionService instance
 */
export function usePermission(): PermissionService {
  // Access from Vue prototype instead of using inject
  const permissionService = Vue.prototype.$permissionService;
  if (!permissionService) {
    throw new Error('Permission service not initialized. Make sure to call providePermission first.');
  }
  return permissionService;
}
